import { useContext } from 'react';
import { createIntl, type IntlShape } from 'react-intl';

import { type Locale, messages } from '../i18n';
import { IntlContext } from '../providers/IntlProvider';

const ICU_ARGUMENT_PATTERN = /\{\w+,\s*(plural|select|selectordinal),/;

const intlByLocale = new Map<Locale, IntlShape>();

const intlFor = (locale: Locale) => {
  let intl = intlByLocale.get(locale);
  if (!intl) {
    intl = createIntl({ locale, messages: messages[locale] });
    intlByLocale.set(locale, intl);
  }
  return intl;
};

export const useIntl = () => {
  const { locale } = useContext(IntlContext);

  const formatMessage = ({ id }: { id: string }, values?: Record<string, string | number>) => {
    let message = messages[locale][id] || id;

    // The `{key}` substitution below cannot evaluate ICU arguments; hand those messages to react-intl.
    if (ICU_ARGUMENT_PATTERN.test(message)) {
      return intlFor(locale).formatMessage({ id }, values);
    }

    if (values) {
      Object.entries(values).forEach(([key, value]) => {
        message = message.replace(`{${key}}`, String(value));
      });
    }

    return message;
  };

  return {
    locale,
    formatMessage,
  };
};
